import {
  ApacCandidateFooter,
  ApacCandidateHeader,
} from '@seek/apac-candidate-header-footer';
import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import { MelwaysProvider } from '@seek/melways-react';
import { VocabProvider } from '@vocab/react';
import { BraidProvider, Page } from 'braid-design-system';
import theme from 'braid-design-system/themes/seekJobs';
import type { RouteObject } from 'react-router-dom';

import ConfigProvider from 'src/provider/config';
import type { RouteProps } from 'src/types';

import { App } from './components/App';
import { appLoader } from './loaders/appLoader';
import { communityExploreGroupsLoader } from './loaders/communityExploreGroupsLoader';
import { communityGroupsLoader } from './loaders/communityGroupsLoader';
import { communityHomeLoader } from './loaders/communityHomeLoader';
import { communityThreadDetailsLoader } from './loaders/communityThreadDetailsLoader';
import { ErrorBoundaryContainer } from './modules/errorBoundary/ErrorBoundaryContainer';
import { CommunityExploreGroups } from './pages/CommunityExploreGroups';
import { CommunityGroups } from './pages/CommunityGroups';
import { CommunityHome } from './pages/CommunityHome';
import { ThreadDetailsPage } from './pages/CommunityThreadDetails';
import { ROUTER_PATHS } from './paths';

export const makeRoutes = ({
  client,
  config,
  clientOrServer,
}: RouteProps): RouteObject[] => [
  {
    path: ROUTER_PATHS.language,
    errorElement: (
      <MelwaysProvider.Server
        site={config.site as any}
        url={config.absoluteUrl}
        staging={config.environment === 'staging'}
      >
        <ConfigProvider config={{ ...config, clientOrServer: 'server' }}>
          <VocabProvider language={config.locale}>
            <BraidProvider theme={theme}>
              <Page
                footer={
                  <ApacCandidateFooter
                    authenticationStatus={AUTHENTICATION_TYPES.AUTH_PENDING}
                    isExpandedVersion={true}
                  />
                }
                footerPosition="belowFold"
              >
                <ApacCandidateHeader
                  authenticationStatus={AUTHENTICATION_TYPES.AUTH_PENDING}
                  leanHeader={false}
                  widthMode="large"
                  activePrimaryTab="community"
                />
                <ErrorBoundaryContainer />
              </Page>
            </BraidProvider>
          </VocabProvider>
        </ConfigProvider>
      </MelwaysProvider.Server>
    ),
    element: (
      <App client={client} config={config} clientOrServer={clientOrServer} />
    ),
    loader: appLoader({ client, config }),
    id: 'app',
    children: [
      {
        path: ROUTER_PATHS.communityHome,
        id: ROUTER_PATHS.communityHome,
        loader: communityHomeLoader({ client, config }),
        element: <CommunityHome />,
      },
      {
        path: ROUTER_PATHS.languageCommunityHome,
        id: ROUTER_PATHS.languageCommunityHome,
        loader: communityHomeLoader({ client, config }),
        element: <CommunityHome />,
      },
      {
        path: ROUTER_PATHS.communityGroupsDetails,
        id: ROUTER_PATHS.communityGroupsDetails,
        loader: communityGroupsLoader({ client, config }),
        element: <CommunityGroups />,
      },
      {
        path: ROUTER_PATHS.languageCommunityGroupsDetails,
        id: ROUTER_PATHS.languageCommunityGroupsDetails,
        loader: communityGroupsLoader({ client, config }),
        element: <CommunityGroups />,
      },
      {
        path: ROUTER_PATHS.communityGroups,
        id: ROUTER_PATHS.communityGroups,
        loader: communityExploreGroupsLoader({ client, config }),
        element: <CommunityExploreGroups />,
      },
      {
        path: ROUTER_PATHS.languageCommunityGroups,
        id: ROUTER_PATHS.languageCommunityGroups,
        loader: communityExploreGroupsLoader({ client, config }),
        element: <CommunityExploreGroups />,
      },
      {
        path: ROUTER_PATHS.communityThreadDetails,
        id: ROUTER_PATHS.communityThreadDetails,
        loader: communityThreadDetailsLoader({ client, config }),
        element: <ThreadDetailsPage />,
      },
      {
        path: ROUTER_PATHS.languageCommunityThreadDetails,
        id: ROUTER_PATHS.languageCommunityThreadDetails,
        loader: communityThreadDetailsLoader({ client, config }),
        element: <ThreadDetailsPage />,
      },
      {
        path: ROUTER_PATHS.communityThreadDetail,
        id: ROUTER_PATHS.communityThreadDetail,
        loader: communityThreadDetailsLoader({ client, config }),
        element: <ThreadDetailsPage />,
      },
      {
        path: ROUTER_PATHS.languageCommunityThreadDetail,
        id: ROUTER_PATHS.languageCommunityThreadDetail,
        loader: communityThreadDetailsLoader({ client, config }),
        element: <ThreadDetailsPage />,
      },
    ],
  },
];
